import React, { useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';  
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import styles from '../FileSelector.module.scss';
import { useMsal } from '@azure/msal-react';
import { useState } from 'react';
import { endpointsCf } from '../../../../config/componentsConfig';
import useFetchWithMsal from '../../../../hooks/useFetchWithMsal';
import { useTasksContext } from '../../../../contexts/TasksContext';
import { TaskType, TaskStatus } from '../../../../contexts/TasksContext';
import { v4 as uuidv4 } from 'uuid';

interface SummaryModalProps {
  fileName: string;
  taskType: string;
  page: string;
  selectedMode: string;
  showSummaryModal: boolean;
  setShowSummaryModal: (show: boolean) => void;
  setSummaryView: (summaryView: {viewed: boolean, doc: string, summary: string}) => void;
  setSelectedMenu: (selectedMenu: number) => void;
  path: string;
  mode?: string;
}

const SummaryModal: React.FC<SummaryModalProps> = ({
  showSummaryModal,
  setShowSummaryModal,
  setSummaryView,
  fileName,
  taskType,
  page,
  selectedMode,
  setSelectedMenu,
  path,
  mode
}) => {

  const { instance } = useMsal();
  const [docSummary, setDocSummary] = useState({title: "", long: "", short: ""});
  const [error, setError] = useState("");
  const { execute } = useFetchWithMsal();
  const activeAccount = instance.getActiveAccount();
  const userId = activeAccount?.username;
  const [loading, setLoading] = useState(false);
  const [summaryAvailable, setSummaryAvailable] = useState("loading"); // loading, available, not available
  const { addTask } = useTasksContext();

  const fileInfo = {
    fileName: fileName,
    user_id: userId,
    mode: mode ? mode : selectedMode === "MYGEORGES" ? "mygeorges" : page
  }


  const hasSummary = useCallback(async () => {

    
    try {
      const response = await execute("POST", endpointsCf.hasSummary, fileInfo)
      console.log("hasSummary :", response);
      return response.hasSummary;
    } catch (error) {   
      console.error("Failed to check summary", error);
      return false;
    }
  }, [execute, fileName, page, selectedMode, userId]);

  const getSummary = useCallback(async () => {
    try {
      const data = await execute("POST", endpointsCf.showSummary, fileInfo);
      setDocSummary({title: fileName, long: data.longSummary, short: data.shortSummary});
      setLoading(false);
    } catch (err) {
      console.error('Summary error:', err);
      setError("Error while getting summary");
      setLoading(false);
    }
  }, [execute, fileName, page, selectedMode, userId]);

  const { selectFilebyName } = useTasksContext();

  const handleAttachSummary = () => {
    setSummaryView({viewed: true, doc: docSummary.title, summary: docSummary.long});
    selectFilebyName(docSummary.title);
    setShowSummaryModal(false);
  }

  useEffect(() => {
    if (fileName && fileName !== "" && showSummaryModal) {
      setLoading(true);
    console.log("Gettig summary for fileName", fileName);
    hasSummary().then((hasSummary) => {
      if (hasSummary) {
        console.log("hasSummary", hasSummary);
        setSummaryAvailable("available");
        getSummary();
      }
      else {
        setLoading(false);
        setSummaryAvailable("not_available");
        console.log("No summary found for fileName", fileName);
      }
      });
    }
    if (fileName === "" || !showSummaryModal) {
      setDocSummary({title: "", long: "", short: ""});
    }
  }, [fileName, userId, page, selectedMode, hasSummary, getSummary, showSummaryModal]);

  const generateSummary = () => {
    const task = {
      id: uuidv4(),
      type: TaskType.SUMMARY,
      timestamp: Date.now(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      path: path,
      file: {
        filename: fileName,
        nbPages: 0,
        nbProcessed: 0,
        nbProcessedSummary: 0,
        status: "completed"
      },
      status: TaskStatus.PROCESSING
    }
    addTask(task)
    setSelectedMenu(5)
    setShowSummaryModal(false)
    setDocSummary({title: "", long: "", short: ""})
    setSummaryAvailable("loading")
    setError("")
  }


  return (
    <Modal 
      show={showSummaryModal} 
      onHide={() => {setShowSummaryModal(false); setDocSummary({title: "", long: "", short: ""}); setSummaryAvailable("loading"); setError("");}}
      size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Summary</Modal.Title>
        <div style={{display: "flex", flex: 1, justifyContent: "flex-end", alignItems: "center", gap: 30}}>
          {summaryAvailable === "available" && selectedMode === "MYGEORGES" && <Button variant="primary" onClick={handleAttachSummary}>View summary and deep-dive in document</Button>}
        </div>
      </Modal.Header>
      <Modal.Body>
        {error}
        { loading ? <div>Loading...</div> : 
          summaryAvailable === "available" ? (
          <>
            <h3>{docSummary.title}</h3>
            <ReactMarkdown
              className={styles.markdown}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeKatex]}
        >
          {docSummary.short}
        </ReactMarkdown>
        <h3>Summary</h3>
        <ReactMarkdown
          className={styles.markdown}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeKatex]}
        >
              {docSummary.long}
            </ReactMarkdown>
          </>
        ) : (
          <>
            <div>No summary found for {fileName}. Would you like to generate one ?</div>
            <Button variant="primary" onClick={generateSummary}>Generate summary</Button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SummaryModal;